import styled from 'particles/themes/styled'
import { CheckSquare, Edit, XSquare, X, XCircle } from 'react-feather'
import React from 'react'
import { Flex, Box, Text, Button, ButtonProps, BoxProps as BoxBaseProps } from 'rebass'
import { theme } from '../theme'
import PrimaryTabActive from '../icons/PrimaryTabActive.png'
import PrimaryTabOrange from '../icons/PrimaryTabOrange.png'
import PrimaryTabRed from '../icons/PrimaryTabRed.png'
import { fontSize, spacing } from 'particles/styles'
import { coreColors } from 'particles/colors'
import ParticleText from 'particles/Text'

export interface ButtonFeProps extends ButtonProps {
    key?: string
    iconSize?: any
    color?: string
    disabled?: boolean
    isActive?: boolean
    textProps?: any
    hoverBg?: string
    text?: string
    textColor?: string
    iconColor?: string
}

interface BoxProps extends BoxBaseProps {
    key?: string
    iconSize?: any
    color?: string
    textProps?: any
    text: string
}

interface StyledProps {
    hoverBg: string
}

const RectButtonCheckSquare: React.FC<ButtonFeProps> = ({
    iconSize = 'sm',
    disabled = false,
    isActive = false,
    textProps,
    hoverBg,
    text,
    ...props
}) => {
    return (
        <>
            <Button
                bg={theme.newColors.neutral100}
                sx={{
                    opacity: disabled ? '60%' : '100%',
                    border: '1px solid #000000',
                    px: 0,
                    py: '5px',
                }}
                {...props}
            >
                <Flex>
                    <Box ml="8px" mr="6px">
                        <CheckSquare
                            width={iconSize}
                            height={iconSize}
                            stroke={isActive ? hoverBg : theme.colors.black}
                        />
                    </Box>
                    <Box mr="8px">
                        <Text fontSize={1} color={isActive ? hoverBg : theme.colors.black} {...textProps}>
                            {text}
                        </Text>
                    </Box>
                </Flex>
            </Button>
        </>
    )
}

const RectButtonEdit: React.FC<ButtonFeProps> = ({
    iconSize = 'sm',
    disabled = false,
    isActive = false,
    textProps,
    hoverBg,
    text,
    ...props
}) => {
    return (
        <>
            <Button
                bg={theme.newColors.neutral50}
                sx={{
                    opacity: disabled ? '60%' : '100%',
                    border: '1px solid #000000',
                    px: 0,
                    py: '5px',
                }}
                {...props}
            >
                <Flex>
                    <Box ml="8px" mr="6px">
                        <Edit width={iconSize} height={iconSize} stroke={isActive ? hoverBg : theme.colors.black} />
                    </Box>
                    <Box mr="8px">
                        <Text fontSize={1} color={isActive ? hoverBg : theme.colors.black} {...textProps}>
                            {text}
                        </Text>
                    </Box>
                </Flex>
            </Button>
        </>
    )
}

const RectButtonXSquare: React.FC<ButtonFeProps> = ({
    iconSize = 'sm',
    disabled = false,
    isActive = false,
    textProps,
    hoverBg,
    text,
    ...props
}) => {
    return (
        <>
            <Button
                bg={theme.newColors.neutral50}
                sx={{
                    opacity: disabled ? '60%' : '100%',
                    border: '1px solid #000000',
                    px: 0,
                    py: '5px',
                }}
                {...props}
            >
                <Flex>
                    <Box ml="8px" mr="6px">
                        <XSquare width={iconSize} height={iconSize} stroke={isActive ? hoverBg : theme.colors.black} />
                    </Box>
                    <Box mr="8px">
                        <Text fontSize={1} color={isActive ? hoverBg : theme.colors.black} {...textProps}>
                            {text}
                        </Text>
                    </Box>
                </Flex>
            </Button>
        </>
    )
}

const ButtonCheckSquare: React.FC<ButtonFeProps> = ({
    iconSize = 'sm',
    disabled = false,
    isActive = false,
    textProps,
    text,
    ...props
}) => (
    <StyledCenterButton bg={theme.newColors.neutral50} {...props}>
        <CheckSquare
            width={iconSize}
            height={iconSize}
            stroke={isActive ? theme.colors.astronaut : theme.colors.black}
        />
        <ParticleText scale="bodyS400" color={isActive ? 'action' : 'primary'} {...textProps}>
            {text}
        </ParticleText>
    </StyledCenterButton>
)

const ButtonCheckSquareActive: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Flex
        alignItems="center"
        justifyContent="center"
        bg={theme.newColors.neutral100}
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabActive})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            gap: spacing[200],
        }}
        {...props}
    >
        <CheckSquare width={iconSize} height={iconSize} stroke={coreColors.blue300} />
        <ParticleText textAlign="center" scale="bodyS500" color="action" {...textProps}>
            {text}
        </ParticleText>
    </Flex>
)

const ButtonCheckSquareOrange: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Box
        bg={theme.newColors.neutral100}
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabOrange})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
        }}
        {...props}
    >
        <Box sx={{ textAlign: 'center' }} pt="0.8rem">
            <CheckSquare width={iconSize} height={iconSize} stroke={theme.colors.orange} />
        </Box>
        <Text pt="0.2rem" textAlign="center" fontSize={2} color={theme.colors.orange} {...textProps}>
            {text}
        </Text>
    </Box>
)

const ButtonCheckSquareRed: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Box
        bg={theme.newColors.neutral100}
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabRed})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
        }}
        {...props}
    >
        <Box sx={{ textAlign: 'center' }} pt="0.8rem">
            <CheckSquare width={iconSize} height={iconSize} stroke={theme.colors.frenchRose} />
        </Box>
        <Text pt="0.2rem" textAlign="center" fontSize={2} color={theme.colors.frenchRose} {...textProps}>
            {text}
        </Text>
    </Box>
)

const ButtonEdit: React.FC<ButtonFeProps> = ({
    iconSize = 'sm',
    disabled = false,
    isActive = false,
    textProps,
    text,
    ...props
}) => (
    <StyledCenterButton disabled={disabled} {...props}>
        <Edit width={iconSize} height={iconSize} stroke={isActive ? theme.colors.astronaut : theme.colors.black} />
        <Text fontSize={fontSize.s} color={isActive ? theme.colors.astronaut : theme.colors.black} {...textProps}>
            {text}
        </Text>
    </StyledCenterButton>
)

const ButtonEditActive: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Box
        bg={theme.newColors.neutral100}
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabActive})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
        }}
        {...props}
    >
        <Edit width={iconSize} height={iconSize} stroke={theme.colors.astronaut} />
        <Text textAlign="center" fontSize={fontSize.s} color={theme.colors.astronaut} {...textProps}>
            {text}
        </Text>
    </Box>
)

const ButtonEditOrange: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Flex
        bg={theme.newColors.neutral100}
        alignItems="center"
        justifyContent="center"
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabOrange})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            gap: spacing[200],
        }}
        {...props}
    >
        <Edit width={iconSize} height={iconSize} stroke={theme.colors.orange} />

        <Text textAlign="center" fontSize={fontSize.s} color={theme.colors.orange} {...textProps}>
            {text}
        </Text>
    </Flex>
)

const ButtonEditRed: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Box
        bg={theme.newColors.neutral100}
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabRed})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
        }}
        {...props}
    >
        <Box sx={{ textAlign: 'center' }} pt="0.8rem">
            <Edit width={iconSize} height={iconSize} stroke={theme.colors.frenchRose} />
        </Box>
        <Text pt="0.2rem" textAlign="center" fontSize={2} color={theme.colors.frenchRose} {...textProps}>
            {text}
        </Text>
    </Box>
)

const ButtonXSquare: React.FC<ButtonFeProps> = ({
    iconSize = 'sm',
    disabled = false,
    isActive = false,
    textProps,
    text,
    ...props
}) => (
    <StyledCenterButton disabled={disabled} {...props}>
        <XSquare width={iconSize} height={iconSize} stroke={isActive ? theme.colors.astronaut : theme.colors.black} />
        <ParticleText scale="bodyS400" color={isActive ? 'action' : 'primary'} {...textProps}>
            {text}
        </ParticleText>
    </StyledCenterButton>
)

const ButtonXSquareActive: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Flex
        bg={theme.newColors.neutral100}
        justifyContent="center"
        alignItems="center"
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabActive})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            gap: spacing[200],
        }}
        {...props}
    >
        <Box sx={{ textAlign: 'center' }} pt="0.8rem">
            <XSquare width={iconSize} height={iconSize} stroke={theme.colors.astronaut} />
        </Box>
        <Text pt="0.2rem" textAlign="center" fontSize={2} color={theme.colors.astronaut} {...textProps}>
            {text}
        </Text>
    </Flex>
)

const ButtonXSquareOrange: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Box
        bg={theme.newColors.neutral100}
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabOrange})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
        }}
        {...props}
    >
        <Box sx={{ textAlign: 'center' }} pt="0.8rem">
            <XSquare width={iconSize} height={iconSize} stroke={theme.colors.orange} />
        </Box>
        <Text pt="0.2rem" textAlign="center" fontSize={2} color={theme.colors.orange} {...textProps}>
            {text}
        </Text>
    </Box>
)

const ButtonXSquareRed: React.FC<BoxProps> = ({
    iconSize = 'sm',
    color = theme.colors.black,
    textProps,
    text,
    ...props
}) => (
    <Flex
        bg={theme.newColors.neutral100}
        justifyContent="center"
        alignItems="center"
        sx={{
            mx: 'auto',
            backgroundImage: `url(${PrimaryTabRed})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            gap: spacing[200],
        }}
        {...props}
    >
        <XSquare width={iconSize} height={iconSize} stroke={theme.colors.frenchRose} />
        <Text textAlign="center" fontSize={2} color={theme.colors.frenchRose} {...textProps}>
            {text}
        </Text>
    </Flex>
)

const StyledCenterButton = styled(Button)<ButtonFeProps>`
    display: flex;
    cursor: pointer;
    justify-content: center;
    opacity: 0.9;
    align-items: center;
    gap: ${spacing[200]};
    padding: 0px;
    background-color: ${coreColors.white50};
    border-radius: 2px;
    border: 2px solid ${coreColors.grey700};
    box-shadow:
        0px 1px 5px 0px rgba(0, 0, 0, 0.12),
        0px 2px 2px 0px rgba(0, 0, 0, 0.14),
        0px 3px 1px -2px rgba(0, 0, 0, 0.2);
`

const SimpleStyledButton = styled.button`
    display: flex;
    &:hover {
        outline: none;
        cursor: pointer;
    }
`

const StyledButton = styled.button`
    &:focus {
        outline: none;
        cursor: pointer;
    }
    &:hover:not(:disabled) {
        box-shadow:
            0px 2px 4px rgba(0, 0, 0, 0.2),
            0px 4px 5px rgba(0, 0, 0, 0.14),
            0px 1px 10px rgba(0, 0, 0, 0.12);
        border: 3px solid ${coreColors.blue300};
    }

    &:hover:not(:disabled) svg {
        stroke: ${coreColors.blue300};
    }

    &:hover:not(:disabled) div {
        color: ${coreColors.blue300};
    }

    &:disabled {
        color: ${coreColors.grey400};
        cursor: not-allowed;
        border: 2px solid ${coreColors.grey400};
    }

    &:disabled svg {
        stroke: ${coreColors.grey400};
    }

    &:disabled div {
        color: ${coreColors.grey400};
    }
`

const StyledRecButton = styled(Button)<StyledProps>`
    &:focus {
        outline: none;
        cursor: pointer;
    }
    &:hover {
        box-shadow: 1px 1px 2px rgba(42, 54, 171, 0.15);
        border: 1px solid ${props => props.hoverBg};
    }
    &:hover svg {
        stroke: ${props => props.hoverBg};
    }
    &:hover div {
        color: ${props => props.hoverBg};
    }
    &:active svg {
        stroke: ${theme.newColors.neutral50};
    }
    &:active div {
        color: ${theme.newColors.neutral50};
    }
    &:active {
        background: ${props => props.hoverBg};
    }
`

const StyledButtonCheckSquare = StyledButton.withComponent(ButtonCheckSquare)
const StyledButtonEdit = StyledButton.withComponent(ButtonEdit)
const StyledButtonXSquare = StyledButton.withComponent(ButtonXSquare)
const StyledRectButtonCheckSquare = StyledRecButton.withComponent(RectButtonCheckSquare)
const StyledRectButtonEdit = StyledRecButton.withComponent(RectButtonEdit)
const StyledRectButtonXSquare = StyledRecButton.withComponent(RectButtonXSquare)
const SimpleButtonXCircle = SimpleStyledButton.withComponent(XCircle)
const SimpleButtonX = SimpleStyledButton.withComponent(X)
const SimpleButtonEdit = SimpleStyledButton.withComponent(Edit)

export {
    StyledRectButtonCheckSquare,
    StyledRectButtonEdit,
    StyledRectButtonXSquare,
    ButtonCheckSquareActive,
    ButtonCheckSquareOrange,
    ButtonCheckSquareRed,
    ButtonEditActive,
    ButtonEditOrange,
    ButtonEditRed,
    ButtonXSquareActive,
    ButtonXSquareOrange,
    ButtonXSquareRed,
    StyledButtonCheckSquare,
    StyledButtonEdit,
    StyledButtonXSquare,
    SimpleButtonXCircle,
    SimpleButtonX,
    SimpleButtonEdit,
}
